import React from 'react';
import { DesencontrosDoRateio } from '../utils/naturezaDoItem';
import { fmtPercentual } from '../utils/rateioDoGrupo';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

/**
 * Os dois desencontros do rateio, no topo dos itens da assinatura. Prévia aprovada
 * em 05/10/2026 (utils/naturezaDoItem):
 *  - "A BASE MUDOU?" (`divergente`, percentual): a base de ramais de hoje daria outra
 *    divisão. Resolve-se no Redividir. O valor em reais dessa divisão é ESTIMATIVA e é dito
 *    assim; não tem a cara de certeza do outro aviso, que compara dois números reais. Vem PRIMEIRO, porque uma divisão nova muda o que o outro compara.
 *  - "O COBRADO HONRA O ACORDO?" (`deltaAcordadoVsCobrado`): os itens cobram outra coisa
 *    que a versão acorda (CONCEITO: 352,91 × 311,39). Resolve-se no rateio do grupo, pelo
 *    "Aplicar as cotas nos contratos" (o motor recusa enquanto os dois não batem).
 */
interface Props {
  desencontros: DesencontrosDoRateio;
  fmt: (v: number) => string;
  onAbrirRateio?: () => void;
}

export default function AvisosDoRateioNaAssinatura({ desencontros, fmt, onAbrirRateio }: Props) {
  const { versaoForaDaBase: vb, itensForaDaVersao: iv } = desencontros;
  if (!vb && !iv) return null;
  const botao = onAbrirRateio && (
    <button type='button' onClick={onAbrirRateio} className='shrink-0 self-start px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 hover:bg-slate-700 text-xs text-slate-200'>
      Abrir o rateio do grupo
    </button>
  );
  return (
    <div className='space-y-2 mb-3'>
      {vb && (
        <div className={`flex gap-3 rounded-lg border p-3 text-sm ${TONE_BORDER.group} ${TONE_SURFACE.group}`} role='status' data-testid='aviso-versao-base'>
          <div className={`flex-1 space-y-1 ${TONE_TEXT.group}`}>
            <p className='font-medium'>A base de ramais mudou depois da divisão vigente.</p>
            <p>
              A divisão vigente dá {fmtPercentual(vb.percentualVigente)} a esta empresa, e hoje se cobram R$ {fmt(vb.valorCobrado)}.
              Pela base de hoje, {vb.ramais} de {vb.ramaisDoGrupo} ramais, seriam {fmtPercentual(vb.percentualCalculado)}, por estimativa cerca de R$ {fmt(vb.valorCalculadoEstimado)} por mês.
              {' '}Isto se resolve redividindo o rateio.
            </p>
          </div>
          {botao}
        </div>
      )}
      {iv && (
        <div className={`flex gap-3 rounded-lg border p-3 text-sm ${TONE_BORDER.warning} ${TONE_SURFACE.warning}`} role='status' data-testid='aviso-itens-versao'>
          <div className={`flex-1 space-y-1 ${TONE_TEXT.warning}`}>
            <p className='font-medium'>O cobrado não honra o acordado.</p>
            <p>
              Somados, cobram R$ {fmt(iv.cobrado)}/mês. A divisão vigente{iv.competenciaInicio ? ` (versão ${iv.competenciaInicio.split('-').reverse().join('/')})` : ''} acorda
              {' '}R$ {fmt(iv.acordado)} ({fmtPercentual(iv.percentualVigente)}): R$ {fmt(Math.abs(iv.diferenca))} {iv.diferenca > 0 ? 'a mais' : 'a menos'} por mês.
            </p>
            {/* ⛔ A frase antiga dizia "com o motor ligado, a fatura passa a cobrar o acordado", e era FALSA justamente
                aqui: o motor é neutro em valor e RECUSA a fatura enquanto o contrato não carrega a cota (09/10/2026, a
                primeira divisão da CONTROLSUL). O conserto é aplicar a cota, pelo rateio do grupo. */}
            <p data-testid='aviso-itens-versao-acao'>A cota acordada é R$ {fmt(iv.acordado)}; o contrato cobra R$ {fmt(iv.cobrado)}. O motor só aplica quando os dois batem — aplique as cotas pelo rateio do grupo.</p>
          </div>
          {botao}
        </div>
      )}
    </div>
  );
}
